import React, { useState, useEffect } from "react";
import { cachedGetDocs } from "./utils/firestoreCache";
import Leaderboard from "./Leaderboard";
import DancerThumbnail from "./DancerThumbnail";
import { getDancerCurrentAgeCategory, shouldResultCountForRankings, batchPrefetchAgeCategoriesFromClaims } from "./utils/ageCategoryUtils";
import "./StarRanking.css";

// Competitions are shared with the other ranking pages through the common cache
const fetchCompetitions = () => cachedGetDocs("competitions");

// ── Constants ────────────────────────────────────────────────────────────────
const latinCategories = [
  "Solo Juvenile I", "Solo Juvenile II", "Solo Junior I",
  "Solo Junior II", "Solo Youth", "Solo Adult"
];
const dances = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];
const contemporaryCategories = [
  "Solo Contemporary Juvenile I", "Solo Contemporary Juvenile II",
  "Solo Contemporary Junior I", "Solo Contemporary Junior II",
  "Solo Contemporary Youth", "Solo Contemporary Adult"
];
const hipHopCategories = [
  "Solo Hip Hop Juvenile I", "Solo Hip Hop Juvenile II",
  "Solo Hip Hop Junior I", "Solo Hip Hop Junior II",
  "Solo Hip Hop Youth", "Solo Hip Hop Adult"
];
const commercialCategories = [
  "Solo Commercial Juvenile I", "Solo Commercial Juvenile II",
  "Solo Commercial Junior I", "Solo Commercial Junior II",
  "Solo Commercial Youth", "Solo Commercial Adult"
];

const DISCIPLINE_ORDER = ["Latin", "Contemporary", "Hip Hop", "Commercial", "Show Dance"];
const DISCIPLINE_COLORS = {
  "Latin":        { bg: "#fff3e0", border: "#f57c00" },
  "Contemporary": { bg: "#e8f5e9", border: "#388e3c" },
  "Hip Hop":      { bg: "#fce4ec", border: "#c2185b" },
  "Commercial":   { bg: "#e3f2fd", border: "#1976d2" },
  "Show Dance":   { bg: "#f3e5f5", border: "#7b1fa2" },
};
const LATIN_DANCE_ORDER = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];
const AGE_CATEGORY_ORDER = ["Adult", "Youth", "Junior II", "Junior I", "Juvenile II", "Juvenile I"];
const RANK_STYLES = [
  { label: "1st", bg: "#FFD700", color: "#7a5a00", icon: "🥇" },
  { label: "2nd", bg: "#E8E8E8", color: "#444",    icon: "🥈" },
  { label: "3rd", bg: "#cd7f32", color: "#fff",    icon: "🥉" },
];

function extractAgeCategory(categoryStr) {
  for (const age of AGE_CATEGORY_ORDER) {
    if (categoryStr.includes(age)) return age;
  }
  return null;
}

// ── Current Stars panel ──────────────────────────────────────────────────────
function CurrentStarsPanel() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState("");
  const [filterDisc, setFilterDisc] = useState("All");
  const [filterAge, setFilterAge]   = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const rankingCutoff = new Date();
        rankingCutoff.setDate(rankingCutoff.getDate() - 365);

        const snap = await fetchCompetitions();

        // First pass: collect valid docs and all dancer names
        const validDocs = [];
        const allDancerNames = new Set();
        snap.forEach(docSnap => {
          const comp = docSnap.data();
          if (!comp.results || !comp.discipline || !comp.category) return;

          const resultDate = comp.timestamp?.toDate
            ? comp.timestamp.toDate()
            : new Date(comp.timestamp || comp.date);
          if (resultDate < rankingCutoff) return;

          validDocs.push(comp);
          for (const result of comp.results) {
            if (result.dancerName && !result.excluded && !result.autoExcluded && !result.invalid && !result.withdrawn) {
              allDancerNames.add(result.dancerName);
            }
          }
        });

        // Fetch current age category for every dancer (same as Leaderboard)
        // First, batch-prefetch from profileClaims in one query to pre-populate the cache,
        // then individual lookups only hit Firestore for dancers without approved claims.
        await batchPrefetchAgeCategoriesFromClaims();
        const dancerAgeCategories = {};
        await Promise.all(
          Array.from(allDancerNames).map(async (name) => {
            dancerAgeCategories[name] = await getDancerCurrentAgeCategory(name);
          })
        );

        // Second pass: group results with age-category filtering
        const groups = new Map();
        for (const comp of validDocs) {
          const multiplier = comp.multiplier || 1;
          const key = comp.dance
            ? `${comp.discipline}_${comp.category}_${comp.dance}`
            : `${comp.discipline}_${comp.category}`;

          if (!groups.has(key)) {
            groups.set(key, {
              discipline: comp.discipline,
              category: comp.category,
              dance: comp.dance || null,
              dancers: new Map(),
            });
          }
          const group = groups.get(key);

          for (const result of comp.results) {
            if (result.excluded || result.autoExcluded || result.invalid || result.withdrawn || !result.dancerName) continue;
            // Skip dancers who have aged out of this category (same logic as Leaderboard)
            if (!shouldResultCountForRankings(comp.category, dancerAgeCategories[result.dancerName])) continue;
            const points = Math.round((result.points || 0) * multiplier);
            if (!group.dancers.has(result.dancerName)) {
              group.dancers.set(result.dancerName, { scores: [], studio: null });
            }
            const dancer = group.dancers.get(result.dancerName);
            dancer.scores.push(points);
            if (result.studio) dancer.studio = result.studio;
          }
        }

        // Compute top-3 per group
        const cats = [];
        for (const [key, group] of groups.entries()) {
          const rankings = [];
          for (const [dancerName, dancer] of group.dancers.entries()) {
            const top6 = [...dancer.scores].sort((a, b) => b - a).slice(0, 6);
            const totalPoints = top6.reduce((s, p) => s + p, 0);
            if (totalPoints > 0) rankings.push({ dancerName, totalPoints, currentStudio: dancer.studio });
          }
          if (rankings.length === 0) continue;

          rankings.sort((a, b) => b.totalPoints - a.totalPoints);
          let currentRank = 1;
          for (let i = 0; i < rankings.length; i++) {
            if (i > 0 && rankings[i].totalPoints !== rankings[i - 1].totalPoints) currentRank = i + 1;
            rankings[i].currentRank = currentRank;
          }

          const top3 = rankings.filter(r => r.currentRank <= 3).slice(0, 3);
          if (top3.length === 0) continue;

          const catStr = group.category;
          cats.push({
            id: key,
            discipline: group.discipline,
            category: catStr,
            ageCategory: extractAgeCategory(catStr),
            dance: group.dance,
            totalDancers: rankings.length,
            top3,
          });
        }

        cats.sort((a, b) => {
          const di = DISCIPLINE_ORDER.indexOf(a.discipline) - DISCIPLINE_ORDER.indexOf(b.discipline);
          if (di !== 0) return di;
          const aiAge = AGE_CATEGORY_ORDER.indexOf(a.ageCategory);
          const biAge = AGE_CATEGORY_ORDER.indexOf(b.ageCategory);
          const ageI = (aiAge === -1 ? 999 : aiAge) - (biAge === -1 ? 999 : biAge);
          if (ageI !== 0) return ageI;
          if (a.discipline === "Latin" && a.dance && b.dance) {
            const ai = LATIN_DANCE_ORDER.indexOf(a.dance);
            const bi = LATIN_DANCE_ORDER.indexOf(b.dance);
            if (ai !== -1 || bi !== -1) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
          }
          return (a.dance || "").localeCompare(b.dance || "");
        });
        setCategories(cats);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const disciplines = ["All", ...DISCIPLINE_ORDER.filter(d => categories.some(c => c.discipline === d))];

  const visible = categories.filter(cat => {
    if (filterDisc !== "All" && cat.discipline !== filterDisc) return false;
    if (filterAge  !== "All" && cat.ageCategory  !== filterAge)  return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      cat.category.toLowerCase().includes(q) ||
      cat.discipline.toLowerCase().includes(q) ||
      (cat.dance || "").toLowerCase().includes(q) ||
      cat.top3.some(r => r.dancerName.toLowerCase().includes(q))
    );
  });

  const grouped = {};
  visible.forEach(cat => {
    if (!grouped[cat.discipline]) grouped[cat.discipline] = [];
    grouped[cat.discipline].push(cat);
  });

  if (loading) return (
    <div className="sr-stars__loading">
      <div className="sr-stars__spinner" />
      <p>Loading current stars…</p>
    </div>
  );
  if (error) return <div className="sr-stars__error">⚠️ {error}</div>;

  return (
    <div className="sr-stars">
      {/* Filters */}
      <div className="sr-stars__controls">
        <input
          className="sr-stars__search"
          type="text"
          placeholder="Search discipline, category, dance or dancer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="sr-stars__disc-filters">
          {disciplines.map(d => (
            <button
              key={d}
              className={`sr-stars__disc-btn ${filterDisc === d ? "active" : ""}`}
              onClick={() => setFilterDisc(d)}
              style={d !== "All" && filterDisc === d
                ? { background: DISCIPLINE_COLORS[d]?.border, borderColor: DISCIPLINE_COLORS[d]?.border, color: "#fff" }
                : {}}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="sr-stars__age-filters">
          {["All", ...AGE_CATEGORY_ORDER].map(age => (
            <button
              key={age}
              className={`sr-stars__age-btn ${filterAge === age ? "active" : ""}`}
              onClick={() => setFilterAge(age)}
            >
              {age}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <div className="sr-stars__empty">No categories match your filters.</div>
      )}

      {DISCIPLINE_ORDER.filter(d => grouped[d]).map(disc => {
        const color = DISCIPLINE_COLORS[disc] || { bg: "#f5f5f5", border: "#999" };
        return (
          <section key={disc} className="sr-stars__discipline-section">
            <h3 className="sr-stars__discipline-heading" style={{ borderColor: color.border, color: color.border }}>
              {disc}
              <span className="sr-stars__cat-count">{grouped[disc].length}</span>
            </h3>

            {disc === "Latin" ? (
              (() => {
                const byAge = {};
                grouped[disc].forEach(cat => {
                  const age = cat.ageCategory || "Other";
                  if (!byAge[age]) byAge[age] = [];
                  byAge[age].push(cat);
                });
                return [...AGE_CATEGORY_ORDER, "Other"].filter(a => byAge[a]).map(age => (
                  <div key={age} className="sr-stars__age-row">
                    <div className="sr-stars__age-label">{age}</div>
                    <div className="sr-stars__grid sr-stars__grid--latin">
                      {byAge[age].map(cat => <StarCard key={cat.id} cat={cat} color={color} />)}
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div className="sr-stars__grid">
                {grouped[disc].map(cat => <StarCard key={cat.id} cat={cat} color={color} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function StarCard({ cat, color }) {
  const label = cat.dance
    ? cat.dance
    : cat.category.replace(/^Solo\s+/i, "").replace(new RegExp(`^${cat.discipline}\\s*`, "i"), "").trim() || cat.category;

  return (
    <div className="sr-stars__card" style={{ borderColor: color.border, background: color.bg }}>
      <div className="sr-stars__card-header" style={{ borderBottomColor: color.border }}>
        <span className="sr-stars__card-label">{label}</span>
        <span className="sr-stars__card-total">{cat.totalDancers} dancers</span>
      </div>
      <div className="sr-stars__podium">
        {[0, 1, 2].map(i => {
          const dancer = cat.top3[i];
          const rs = RANK_STYLES[i];
          return (
            <div key={i} className={`sr-stars__slot sr-stars__slot--${i + 1}`}>
              {dancer ? (
                <>
                  <div className="sr-stars__rank-badge" style={{ background: rs.bg, color: rs.color }}>{rs.icon}</div>
                  <DancerThumbnail dancerName={dancer.dancerName} size={40} className="sr-stars__thumb" />
                  <div className="sr-stars__dancer-info">
                    <a href={`/dancer/${encodeURIComponent(dancer.dancerName)}`}
                       className="sr-stars__dancer-name" target="_blank" rel="noreferrer">
                      {dancer.dancerName}
                    </a>
                    {dancer.currentStudio && (
                      <span className="sr-stars__studio">{dancer.currentStudio}</span>
                    )}
                    <div className="sr-stars__meta">
                      <span className="sr-stars__points">{dancer.totalPoints.toLocaleString()} pts</span>
                      {dancer.currentDaysHeld > 0 && <span className="sr-stars__days">⏱ {dancer.currentDaysHeld}d</span>}
                    </div>
                  </div>
                </>
              ) : (
                <div className="sr-stars__empty-slot">
                  <span style={{ fontSize: "1.2rem", opacity: 0.25 }}>{rs.icon}</span>
                  <span style={{ fontSize: "0.75rem", color: "#aaa" }}>—</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Rising Stars panel ───────────────────────────────────────────────────────
function RisingStarsPanel() {
  const [ageCategoryData, setAgeCategoryData] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [filterAge, setFilterAge]             = useState("All");

  useEffect(() => {
    (async () => {
      try {
        const rankingCutoff = new Date();
        rankingCutoff.setDate(rankingCutoff.getDate() - 365);

        const snap = await fetchCompetitions();

        // First pass: collect valid Latin docs and all dancer names
        const validDocs = [];
        const allDancerNames = new Set();
        snap.forEach(docSnap => {
          const comp = docSnap.data();
          if (!comp.results || comp.discipline !== "Latin" || !comp.category || !comp.dance) return;
          const resultDate = comp.timestamp?.toDate
            ? comp.timestamp.toDate()
            : new Date(comp.timestamp || comp.date);
          if (resultDate < rankingCutoff) return;
          validDocs.push(comp);
          for (const result of comp.results) {
            if (result.dancerName && !result.excluded && !result.autoExcluded && !result.invalid && !result.withdrawn) {
              allDancerNames.add(result.dancerName);
            }
          }
        });

        // Batch prefetch age categories (same optimisation as Stars Only)
        await batchPrefetchAgeCategoriesFromClaims();
        const dancerAgeCategories = {};
        await Promise.all(
          Array.from(allDancerNames).map(async (name) => {
            dancerAgeCategories[name] = await getDancerCurrentAgeCategory(name);
          })
        );

        // Second pass: build per-category+dance score maps with age filtering
        const groups = new Map();
        for (const comp of validDocs) {
          const multiplier = comp.multiplier || 1;
          const key = `${comp.category}_${comp.dance}`;
          if (!groups.has(key)) {
            groups.set(key, {
              category: comp.category,
              dance: comp.dance,
              ageCategory: extractAgeCategory(comp.category),
              dancers: new Map(),
            });
          }
          const group = groups.get(key);
          for (const result of comp.results) {
            if (result.excluded || result.autoExcluded || result.invalid || result.withdrawn || !result.dancerName) continue;
            if (!shouldResultCountForRankings(comp.category, dancerAgeCategories[result.dancerName])) continue;
            const points = Math.round((result.points || 0) * multiplier);
            if (!group.dancers.has(result.dancerName)) {
              group.dancers.set(result.dancerName, { scores: [], studio: null });
            }
            const dancer = group.dancers.get(result.dancerName);
            dancer.scores.push(points);
            if (result.studio) dancer.studio = result.studio;
          }
        }

        // For each category+dance, rank dancers and collect top-6 per age category
        const qualifiedByAge = new Map(); // ageCategory → Map<dancerName, {studio, qualifiedDances[], bestRank}>
        for (const [, group] of groups.entries()) {
          const { ageCategory, dance, dancers } = group;
          if (!ageCategory) continue;

          const rankings = [];
          for (const [dancerName, dancer] of dancers.entries()) {
            const top4 = [...dancer.scores].sort((a, b) => b - a).slice(0, 4);
            const totalPoints = top4.reduce((s, p) => s + p, 0);
            if (totalPoints > 0) rankings.push({ dancerName, totalPoints, studio: dancer.studio });
          }
          rankings.sort((a, b) => b.totalPoints - a.totalPoints);

          // Assign ranks with tie handling
          let lastPoints = null;
          let lastRank = 0;
          let tieCount = 0;
          for (let i = 0; i < rankings.length; i++) {
            if (lastPoints === rankings[i].totalPoints) {
              rankings[i].rank = lastRank;
              tieCount++;
            } else {
              rankings[i].rank = lastRank + tieCount + 1;
              lastRank = rankings[i].rank;
              lastPoints = rankings[i].totalPoints;
              tieCount = 0;
            }
          }

          // Collect all dancers who are rank ≤ 6 in this dance
          for (const r of rankings.filter(r => r.rank <= 6)) {
            if (!qualifiedByAge.has(ageCategory)) qualifiedByAge.set(ageCategory, new Map());
            const ageMap = qualifiedByAge.get(ageCategory);
            if (!ageMap.has(r.dancerName)) {
              ageMap.set(r.dancerName, { studio: r.studio, qualifiedDances: [], bestRank: r.rank });
            }
            const entry = ageMap.get(r.dancerName);
            entry.qualifiedDances.push(dance);
            if (r.rank < entry.bestRank) entry.bestRank = r.rank;
            if (r.studio) entry.studio = r.studio;
          }
        }

        // Convert to array sorted by AGE_CATEGORY_ORDER
        const result = AGE_CATEGORY_ORDER
          .filter(age => qualifiedByAge.has(age))
          .map(age => {
            const ageMap = qualifiedByAge.get(age);
            const dancers = Array.from(ageMap.entries())
              .map(([name, data]) => ({
                dancerName: name,
                studio: data.studio,
                qualifiedDances: [...data.qualifiedDances].sort(
                  (a, b) => LATIN_DANCE_ORDER.indexOf(a) - LATIN_DANCE_ORDER.indexOf(b)
                ),
                bestRank: data.bestRank,
              }))
              .sort((a, b) => a.bestRank - b.bestRank || a.dancerName.localeCompare(b.dancerName));
            return { ageCategory: age, dancers };
          });

        setAgeCategoryData(result);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = filterAge === "All"
    ? ageCategoryData
    : ageCategoryData.filter(a => a.ageCategory === filterAge);

  if (loading) return (
    <div className="sr-stars__loading">
      <div className="sr-stars__spinner" />
      <p>Loading Rising Stars…</p>
    </div>
  );
  if (error) return <div className="sr-stars__error">⚠️ {error}</div>;

  return (
    <div className="sr-rising">
      {/* Age filter */}
      <div className="sr-rising__controls">
        <div className="sr-stars__age-filters">
          {["All", ...AGE_CATEGORY_ORDER].map(age => (
            <button
              key={age}
              className={`sr-stars__age-btn ${filterAge === age ? "active" : ""}`}
              onClick={() => setFilterAge(age)}
            >
              {age}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <div className="sr-stars__empty">No qualified dancers found.</div>
      )}

      {visible.map(({ ageCategory, dancers }) => (
        <section key={ageCategory} className="sr-rising__section">
          <div className="sr-rising__section-header">
            <h3 className="sr-rising__age-heading">Latin — {ageCategory}</h3>
            <span className="sr-rising__count">{dancers.length} dancer{dancers.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="sr-rising__disqualified-label">
            ⛔ The following dancers are not allowed to dance in Rising Stars
          </div>
          <div className="sr-rising__list">
            {dancers.map((dancer, idx) => (
              <div key={dancer.dancerName} className="sr-rising__row">
                <span className="sr-rising__num">{idx + 1}</span>
                <DancerThumbnail dancerName={dancer.dancerName} size={36} className="sr-rising__thumb" />
                <div className="sr-rising__info">
                  <a
                    href={`/dancer/${encodeURIComponent(dancer.dancerName)}`}
                    className="sr-rising__name"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {dancer.dancerName}
                  </a>
                  {dancer.studio && <span className="sr-rising__studio">{dancer.studio}</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Main StarRanking component ───────────────────────────────────────────────
export default function StarRanking() {
  const [mode, setMode]         = useState("leaderboard"); // "leaderboard" | "stars" | "rising"
  const [discipline, setDiscipline] = useState(null);
  const [category, setCategory]     = useState("");
  const [dance, setDance]           = useState("");

  return (
    <div className="sr-page">
      {/* Toggle replaces the headline */}
      <div className="sr-toggle">
        <button
          className={`sr-toggle__btn ${mode === "leaderboard" ? "active" : ""}`}
          onClick={() => setMode("leaderboard")}
        >
          📊 Leaderboard
        </button>
        <button
          className={`sr-toggle__btn ${mode === "stars" ? "active" : ""}`}
          onClick={() => setMode("stars")}
        >
          ⭐ Stars Only
        </button>
        <button
          className={`sr-toggle__btn ${mode === "rising" ? "active" : ""}`}
          onClick={() => setMode("rising")}
        >
          🌟 Rising Stars
        </button>
      </div>

      {/* ── Leaderboard mode ─────────────────────────────────────────────── */}
      {mode === "leaderboard" && (
        <div className="sr-leaderboard-panel">
          <div className="sr-disc-row">
            {["Latin", "Contemporary", "Hip Hop", "Commercial"].map((d) => (
              <button
                key={d}
                className={`sr-disc-btn ${discipline === d ? "active" : ""}`}
                onClick={() => { setDiscipline(d); setCategory(""); setDance(""); }}
              >
                {d}
              </button>
            ))}
          </div>

          {discipline === "Latin" && (
            <div className="sr-selects">
              <select className="sr-select" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Select Category</option>
                {latinCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              {category && (
                <select className="sr-select" value={dance} onChange={e => setDance(e.target.value)}>
                  <option value="">Select Dance</option>
                  {dances.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              )}
            </div>
          )}

          {(discipline === "Contemporary" || discipline === "Hip Hop" || discipline === "Commercial") && (
            <div className="sr-selects">
              <select className="sr-select" value={category} onChange={e => setCategory(e.target.value)}>
                <option value="">Select Category</option>
                {(discipline === "Contemporary" ? contemporaryCategories
                  : discipline === "Hip Hop" ? hipHopCategories
                  : commercialCategories
                ).map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          )}

          {discipline && category && (discipline !== "Latin" || dance) && (
            <div className="sr-leaderboard-wrap">
              <Leaderboard
                discipline={discipline}
                category={category}
                dance={discipline === "Latin" ? dance : null}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Current Stars mode — always mounted so it loads in background ── */}
      <div style={{ display: mode === "stars" ? "block" : "none" }}>
        <CurrentStarsPanel />
      </div>

      {/* ── Rising Stars mode — always mounted so it loads in background ── */}
      <div style={{ display: mode === "rising" ? "block" : "none" }}>
        <RisingStarsPanel />
      </div>
    </div>
  );
}
